import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import Table from "../../components/table/Table";
import SectionHeading from "../../components/SectionHeading";
import ReactTooltip from "react-tooltip";
import GTM from "../../GTM";
import PropTypes from "prop-types";

function ClaimLeaveHistoryTable({
    handleLeaveNoSelection,
    handleClaimNoSelection,
    handleClaimHistoryData,
    handleSelectedOrgId,
    employeeLeaves,
    isLoading,
    print,
}) {
    const [showHistoricalLeaves, setShowHistoricalLeaves] = useState(!!print);
    const [totalHistoricalLeaves, setTotalHistoricalLeaves] = useState(0);
    const [data, setData] = useState([]);
    const [printData, setPrintData] = useState([]);
    const [tooltip, setTooltip] = useState(true);
    const [historicalLeaveMsg, setHistoricalLeaveMsg] = useState(
        "No historical leaves to display"
    );

    const gtm = new GTM(null);
    useEffect(() => {
        updateData(employeeLeaves);
        const historicalLeave = employeeLeaves.find(
            (d) => d.Historical === true
        );

        const countLeaveHistoricalINDY = employeeLeaves.filter(
            (leave) => leave.LeaveHistoricalIND === "N"
        ).length;

        setShowHistoricalLeaves(
            countLeaveHistoricalINDY === 0 && historicalLeave
        );
    }, [employeeLeaves]);

    useEffect(() => {
        if (isLoading === false) {
            const historicalLeave = employeeLeaves.find(
                (d) => d.Historical === true
            );

            if (historicalLeave || print === true) {
                setTotalHistoricalLeaves(1);
                setHistoricalLeaveMsg(
                    "Click here to view historical leaves <br /> in the history table"
                );
            }
        }
    }, [isLoading]);

    useEffect(() => {
        if (handleClaimHistoryData) handleClaimHistoryData(printData);
    }, [printData]);

    const sortColumnHeaderKeyMapping = {
        Coverage: "Coverage",
        Status: "CurrentStatus",
        "Claim / Leave #": "No",
        "Policy Div / Report Grp": "Group",
        "RTW Status": "RTWStatus",
        "Status Reason": "StatusReason",
        "Leave Reason / Diagnosis": "LeaveReason",

        "Status Date": "CurrentStatusDate",
        "Received Date": "ReceivedDate",
        "Last Day Worked": "LastWorkDate",
        "Start Date": "StartDate",
        "End Date": "EndDate",
        "Max Dur.": "MaximumDuration",
        "RTW Date": "RTWDate",
        "Disability Date": "Disability",
    };

    // Helper functions to reduce cognitive complexity of customSortFn
    const compareNullOrUndefined = (aField, bField, isDesc) => {
        if (aField === null || aField === undefined) return isDesc ? -1 : 1;
        if (bField === null || bField === undefined) return isDesc ? 1 : -1;
        return null; // No comparison made
    };

    const compareDates = (aField, bField, isDesc) => {
        const aDate = new Date(aField);
        const bDate = new Date(bField);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
            return isDesc ? bDate - aDate : aDate - bDate;
        }
        return null; // No comparison made
    };

    const compareNumbers = (aField, bField, isDesc) => {
        const aNumber = parseFloat(aField);
        const bNumber = parseFloat(bField);
        if (!isNaN(aNumber) && !isNaN(bNumber)) {
            return isDesc ? bNumber - aNumber : aNumber - bNumber;
        }
        return null; // No comparison made
    };

    const compareStrings = (aField, bField, isDesc) => {
        let aString =
            typeof aField === "string" ? aField.toLowerCase() : aField;
        let bString =
            typeof bField === "string" ? bField.toLowerCase() : bField;

        if (aString > bString) return isDesc ? -1 : 1;
        if (aString < bString) return isDesc ? 1 : -1;
        return 0; // Equal values
    };

    // Refactored function with reduced cognitive complexity
    const customSortFn = (rows, sortBy, sortByKey) => {
        return rows.sort((rowA, rowB) => {
            let aField = rowA[sortByKey];
            let bField = rowB[sortByKey];

            // Try each comparison in sequence
            const nullComparison = compareNullOrUndefined(
                aField,
                bField,
                sortBy.desc
            );
            if (nullComparison !== null) return nullComparison;

            const dateComparison = compareDates(aField, bField, sortBy.desc);
            if (dateComparison !== null) return dateComparison;

            const numberComparison = compareNumbers(
                aField,
                bField,
                sortBy.desc
            );
            if (numberComparison !== null) return numberComparison;

            // Default to string comparison
            return compareStrings(aField, bField, sortBy.desc);
        });
    };

    const handleSort = useCallback(
        (sortBy) => {
            if (sortBy.length === 0) {
                updateData(employeeLeaves);
                return;
            }

            if (sortBy[0].id in sortColumnHeaderKeyMapping) {
                const historicalRecords = employeeLeaves.filter(
                    (row) => row.Historical === true
                );

                const nonHistoricalRecords = employeeLeaves.filter(
                    (row) => row.Historical === false
                );

                const sortedEmployees = [
                    ...customSortFn(
                        historicalRecords,
                        sortBy[0],
                        sortColumnHeaderKeyMapping[sortBy[0].id]
                    ),
                    ...customSortFn(
                        nonHistoricalRecords,
                        sortBy[0],
                        sortColumnHeaderKeyMapping[sortBy[0].id]
                    ),
                ];

                setPrintData(sortedEmployees);

                const d = showHistoricalLeaves
                    ? sortedEmployees
                    : sortedEmployees.filter(
                          (leave) => leave.Historical !== true
                      );

                setData(d);
            }
        },
        [employeeLeaves, showHistoricalLeaves]
    );

    const updateData = (records) => {
        // Default sort: most recent Received Date first, historical rows kept on top
        const defaultSort = { desc: true };
        const historicalRecords = customSortFn(
            records.filter((row) => row.Historical === true),
            defaultSort,
            "ReceivedDate"
        );
        const nonHistoricalRecords = customSortFn(
            records.filter((row) => row.Historical !== true),
            defaultSort,
            "ReceivedDate"
        );

        if (print !== true) {
            const recordsWithHistorical = [
                ...historicalRecords,
                ...nonHistoricalRecords,
            ];

            setPrintData(recordsWithHistorical);

            const d = showHistoricalLeaves
                ? recordsWithHistorical
                : nonHistoricalRecords;

            setData(d);
        } else {
            setData([...historicalRecords, ...nonHistoricalRecords]);
        }
    };

    const getTrClasses = (row) => {
        return row.original.Historical === true ? "tr historical-tr" : "tr";
    };

    const handelClick = (label, attribute3) => {
        gtm.Event(
            "claim & leave",
            "button",
            "click",
            label,
            "employee history",
            "",
            attribute3,
            ""
        );
    };

    const ShowHistoricalLeaveAction = (
        <div
            style={{
                cursor: totalHistoricalLeaves === 0 ? "default" : "pointer",
                color: totalHistoricalLeaves === 0 ? "#AAAAAA" : "#025780",
            }}
            tabIndex="0"
        >
            <i
                className="fa-solid fa-info-circle"
                data-testid="info-icon"
                style={{ color: "#015294", fontSize: 16, paddingRight: 10 }}
                onMouseEnter={() => setTooltip(true)}
                onMouseLeave={() => {
                    setTooltip(false);
                    setTimeout(() => setTooltip(true), 50);
                }}
                data-tip
                data-for="tipForHistoricalLeave"
            ></i>
            <span
                style={{ textDecoration: "underline", fontWeight: 600 }}
                onClick={() => {
                    handelClick(
                        "Show Historical Leaves",
                        "Show_Historical_Leaves"
                    );
                }}
            >
                Show Historical Leaves
            </span>
            {tooltip && (
                <ReactTooltip
                    id="tipForHistoricalLeave"
                    place="bottom"
                    effect="solid"
                    backgroundColor="#015294"
                    className="tooltipStyle"
                    html={true}
                >
                    {historicalLeaveMsg}
                </ReactTooltip>
            )}
            <i
                className="fa-solid fa-circle-arrow-right ml-10p"
                onClick={() => {
                    handelClick("front arrow", "Show_Historical_Leaves");
                }}
            ></i>
        </div>
    );

    const HideHistoricalLeaveAction = (
        <div style={{ cursor: "pointer", color: "#025780" }}>
            <span
                style={{ textDecoration: "underline", fontWeight: 600 }}
                onClick={() => {
                    handelClick(
                        "Hide Historical Leaves",
                        "Hide_Historical_Leaves"
                    );
                }}
            >
                Hide Historical Leaves
            </span>
            <i
                className="fa-solid fa-circle-arrow-right ml-10p"
                style={{
                    transform: "rotate(180deg)",
                }}
                onClick={() => {
                    handelClick("Back arrow", "Hide_Historical_Leaves");
                }}
            ></i>
        </div>
    );

    let returnRecord = (isClaim, rowNum, orgIdNo) => {
        if (isClaim) {
            handleClaimNoSelection(rowNum);
            handleSelectedOrgId(orgIdNo);
        } else {
            handleLeaveNoSelection(rowNum);
            handleSelectedOrgId(orgIdNo);
        }
    };

    const formattedDate = (dateObject) => {
        return dateObject ? moment(dateObject).format("MM/DD/YYYY") : "";
    };

    function claimLeaveNo(row) {
        return (
            <span
                onClick={() => {
                    returnRecord(row.isClaim, row.No, row.OrganizationId);
                    gtm.Event(
                        "claim & leave",
                        "link",
                        "click",
                        "claim/leave",
                        "employee history",
                        "",
                        "field_interaction",
                        `${row.Coverage.toLowerCase()}`
                    );
                }}
                style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                }}
                className="sticky-no"
                tabIndex={0}
            >
                {row.No}
            </span>
        );
    }

    const computedClaimWidth = React.useMemo(() => {
        if (!employeeLeaves || employeeLeaves.length === 0) return 100;
        const maxLength = employeeLeaves.reduce((max, row) => {
            const length = row.No?.toString().length || 0;
            return Math.max(max, length);
        }, 0);

        return maxLength < 22 ? 100 : 165;
    }, [employeeLeaves]);

    const getLeaveReasonDiagnosis = () => {
        return (
            <span>
                Leave Reason / <br /> Diagnosis
            </span>
        );
    };

    const getLeaveReasonDiagnosisCell = ({ row }) => {
        return (
            <span
                style={{
                    whiteSpace: print === true ? "normal" : "auto",
                }}
            >
                {row.original.LeaveReason}
            </span>
        );
    };

    const tableColumns = React.useMemo(
        () => [
            {
                Header: "Employee History",
                sticky: "left",
                columns: [
                    {
                        Header: "Claim / Leave #",
                        width: computedClaimWidth,
                        tabIndex: 0,
                        accessor: (row) => {
                            return claimLeaveNo(row);
                        },
                    },
                ],
            },
            {
                Header: "B",
                columns: [
                    {
                        Header: "Policy Div / Report Grp",
                        accessor: (row) => row.Group,
                        sortable: true,
                        wrap: true,
                        width: print === true ? 160 : 110,
                        tabIndex: 0,
                    },
                    {
                        Header: "Coverage",
                        accessor: (row) => row.Coverage,
                        sortable: true,
                        wrap: true,
                        width: 80,
                        tabIndex: 0,
                    },
                    {
                        Header: "Status",
                        accessor: (row) => row.CurrentStatus,
                        sortable: true,
                        wrap: true,
                        maxWidth: 80,
                        minWidth: 80,
                        width: 80,
                        tabIndex: 0,
                    },
                    {
                        Header: "Status Reason",
                        accessor: (row) => row.StatusReason,
                        sortable: true,
                        wrap: true,

                        width: print === true ? 100 : 90,
                        tabIndex: 0,
                    },
                    {
                        id: "Leave Reason / Diagnosis",
                        Header: getLeaveReasonDiagnosis,
                        Cell: getLeaveReasonDiagnosisCell,
                        sortable: true,
                        wrap: true,
                        canSort: true,
                        width: print ? 150 : 130,
                        tabIndex: 0,
                    },
                    {
                        Header: "Status Date",
                        accessor: (row) => formattedDate(row.CurrentStatusDate),
                        sortable: true,
                        width: 120,
                        tabIndex: 0,
                    },
                    {
                        Header: "Received Date",
                        accessor: (row) => formattedDate(row.ReceivedDate),
                        sortable: true,
                        width: 90,
                        tabIndex: 0,
                    },
                    {
                        Header: "Disability Date",
                        accessor: (row) => formattedDate(row.Disability),
                        sortable: true,
                        maxWidth: 90,
                        minWidth: 90,
                        width: 90,
                        tabIndex: 0,
                    },
                    {
                        Header: "Last Day Worked",
                        accessor: (row) => formattedDate(row.LastWorkDate),
                        sortable: true,
                        maxWidth: 90,
                        minWidth: 90,
                        width: 90,
                        tabIndex: 0,
                    },
                    {
                        Header: "Start Date",
                        accessor: (row) => formattedDate(row.StartDate),
                        sortable: true,
                        width: 100,
                        tabIndex: 0,
                    },
                    {
                        Header: "End Date",
                        accessor: (row) => formattedDate(row.EndDate),
                        sortable: true,
                        width: 100,
                        tabIndex: 0,
                    },
                    {
                        Header: "Max Dur.",
                        accessor: (row) => formattedDate(row.MaximumDuration),
                        sortable: true,
                        width: 100,
                        tabIndex: 0,
                    },
                    {
                        Header: "RTW Date",
                        accessor: (row) => row.RTWDate,
                        sortable: true,
                        width: 100,
                        tabIndex: 0,
                    },
                    {
                        Header: "RTW Status",
                        accessor: (row) => row.RTWStatus,
                        sortable: true,
                        wrap: true,
                        maxWidth: 90,
                        minWidth: 90,
                        width: 90,
                        tabIndex: 0,
                    },
                ],
            },
        ],
        [employeeLeaves]
    );

    return (
        <div
            className={
                "section-card px-0 employee-history-table " +
                (!print && data.length <= 5 ? "pb-0" : "")
            }
        >
            <div className="row">
                <div className="col-md-12">
                    <div className="d-flex justify-content-between align-items-start px-20p">
                        <SectionHeading heading="Employee History" />

                        <span
                            onClick={
                                totalHistoricalLeaves === 0
                                    ? null
                                    : () => {
                                          setShowHistoricalLeaves(
                                              !showHistoricalLeaves
                                          );
                                      }
                            }
                            id="show-hide-historical-btn"
                        >
                            {showHistoricalLeaves
                                ? HideHistoricalLeaveAction
                                : ShowHistoricalLeaveAction}
                        </span>
                    </div>

                    {employeeLeaves.length > 0 && (
                        <Table
                            columns={tableColumns}
                            className="page-brake"
                            data={data}
                            onSort={handleSort}
                            getTrClasses={getTrClasses}
                            manualSortBy={true}
                            sticky={true}
                            loading={isLoading}
                            pagination={!print && data.length > 5}
                            responsiveConfig={{
                                type: "COLUMN",
                                striped: false,
                                screen: "md",
                            }}
                            attribute1="employee history"
                            pageName="claim & leave"
                            numberOfRecords={10}
                            defaultPageSize={5}
                            style="d-flex"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

ClaimLeaveHistoryTable.propTypes = {
    handleLeaveNoSelection: PropTypes.func,
    handleClaimNoSelection: PropTypes.func,
    handleClaimHistoryData: PropTypes.func,
    handleSelectedOrgId: PropTypes.func,
    employeeLeaves: PropTypes.array,
    isLoading: PropTypes.bool,
    print: PropTypes.bool,
};

export default React.memo(ClaimLeaveHistoryTable);
